export const isLocalHost = () => {
	return ['localhost', '127.0.0.1', '10.0.2.2'].includes(
		window.location.hostname
	);
};

/**
 * Extracts the base domain (protocol + host) from an API URL.
 * Removes any `/api/...` or trailing slash at the end.
 *
 * Examples:
 *   "https://example.com/api/v1/"  -> "https://example.com"
 *   "https://sub.domain.com/api"   -> "https://sub.domain.com"
 *   "http://localhost:8000/"       -> "http://localhost:8000"
 */
export function getBaseDomain(apiURL: string): string {
	try {
		const url = new URL(apiURL);

		// Rebuild only origin (protocol + host + port if present)
		return url.origin;
	} catch (e) {
		console.error('Invalid API URL:', apiURL, e);
		return apiURL;
	}
}

export const getHubURL = (apiBaseURL: string) => {
	return apiBaseURL.replace('/api/v1', '/hub');
};

/**
 * URL of the backend login page that starts the "Login with 360dialog" SSO
 * flow right away (?sso=360dialog) and sends the user back to redirectUrl
 * with a refresh_token query parameter appended after a successful login.
 * With switchAccount, the current 360dialog session is logged out first so
 * that 360dialog offers picking an account instead of silently reusing it.
 */
export const get360dialogLoginPageURL = (
	apiBaseURL: string,
	redirectUrl: string,
	switchAccount = false
) => {
	// apiBaseURL can be relative (same-origin deployments, e.g. "/api/v1/")
	const origin = new URL(apiBaseURL, window.location.origin).origin;
	return `${origin}/api/v1/auth/login/?sso=360dialog&next=${encodeURIComponent(
		redirectUrl
	)}${switchAccount ? '&sso_switch=1' : ''}`;
};

export const getBaseURL = () => {
	const windowLocation = window.location;
	return windowLocation.protocol + '//' + windowLocation.host + '/';
};

export const getWebSocketURL = (apiBaseURL: string) => {
	if (
		!apiBaseURL ||
		apiBaseURL === '/' ||
		apiBaseURL === '/api/v1/' ||
		apiBaseURL === '/api/v2/'
	) {
		const baseURL = getBaseURL();
		return prepareWebSocketURL(baseURL);
	} else {
		return prepareWebSocketURL(apiBaseURL);
	}
};

const prepareWebSocketURL = (url: string) => {
	return url
		.replace('https://', 'wss://websockets-')
		.replace('http://', 'wss://websockets-')
		.replace('api/v1/', '')
		.replace('api/v2/', '');
};

export const getURLParams = () => new URLSearchParams(window.location.search);

export const getIntegrationApiBaseURL = () =>
	prepareApiBaseURL(getURLParams().get('integration_api_base_url'));

export const prepareApiBaseURL = (url: string | null) => {
	if (!url) return;

	if (url.startsWith('http:')) {
		url = url.replace('http:', 'https:');
	}

	if (!url.startsWith('https://')) {
		url = 'https://' + url;
	}

	try {
		const result = new URL(url);
		return result.origin + '/api/v1/';
	} catch (e) {
		console.error(e);
	}
};

export const prepareURLForDisplay = (url: string) => {
	if (['/api/v1/', '/api/v2/'].includes(url)) {
		return 'localhost';
	}

	return url
		.replace('http://', '')
		.replace('https://', '')
		.replace('/api/v1/', '')
		.replace('/api/v2/', '');
};
